import type * as THREE from 'three';


/**
 * 草图线材质深度策略。
 *
 * 职责：
 * 1. 将正式草图边、交互状态线和绘制期预览线统一设为前景覆盖材质；
 * 2. 同时关闭深度测试与深度写入，避免不同创建链路产生不一致的遮挡行为；
 * 3. 保持传入材质实例及数组引用不变，避免破坏既有缓存与资源所有权。
 */
export class SketchLineMaterialDepthPolicy {
    /**
     * 对单个草图线材质应用深度策略。
     *
     * @param material 待处理的草图线材质。
     * @returns 原草图线材质实例。
     */
    public static apply<TMaterial extends THREE.Material>(material: TMaterial): TMaterial;

    /**
     * 对草图线多材质数组应用深度策略。
     *
     * @param materials 待处理的草图线材质数组。
     * @returns 原草图线材质数组。
     */
    public static apply<TMaterial extends THREE.Material>(materials: TMaterial[]): TMaterial[];

    /**
     * 对草图线材质或材质数组应用统一深度策略。
     *
     * 设计说明：
     * 1. 显式同时关闭两个开关，避免依赖渲染器对 `depthTest = false` 的隐式行为；
     * 2. 仅在状态发生变化时标记材质更新，减少共享材质被重复刷新；
     * 3. 不修改颜色、透明度和线型，保持本类只有深度策略职责。
     *
     * @param materialOrMaterials 待处理的单材质或多材质数组。
     * @returns 原材质或原材质数组。
     */
    public static apply<TMaterial extends THREE.Material>(
        materialOrMaterials: TMaterial | TMaterial[],
    ): TMaterial | TMaterial[] {
        if (Array.isArray(materialOrMaterials)) {
            // 多材质对象直接遍历原数组，避免高频预览刷新时创建临时容器。
            for (let index = 0; index < materialOrMaterials.length; index += 1) {
                this.applySingleMaterial(materialOrMaterials[index]);
            }
            return materialOrMaterials;
        }

        this.applySingleMaterial(materialOrMaterials);
        return materialOrMaterials;
    }

    /**
     * 对单个材质执行无分配的深度状态修改。
     *
     * @param material 待处理的草图线材质。
     */
    private static applySingleMaterial<TMaterial extends THREE.Material>(material: TMaterial): void {
        const requiresUpdate = material.depthTest || material.depthWrite;

        // 草图线属于确定性的前景覆盖层，不能参与深度比较或污染后续深度缓冲。
        material.depthTest = false;
        material.depthWrite = false;

        if (requiresUpdate) {
            material.needsUpdate = true;
        }
    }
}

